import { PlayerGameStatus, SessionStatus } from '@/API';
import LoadingDots from '@/components/Animated/Icons/LoadingDots';
import PlayerCanvas from '@/components/Player/PlayerCanvas';
import PlayerEndGame from '@/components/Player/PlayerEndGame';
import PlayerSetup from '@/components/Player/PlayerSetup';
import PlayerSpotlight from '@/components/Player/PlayerSpotlight';
import NoSleepProvider from '@/hooks/NoSleep';
import { usePlayer, useSession, useState } from '@/hooks/PlayerSessionControlHooks';
import IntermidiateStep from '@/views/IntermidiateStep';
import { useParams } from 'react-router-dom';
import { getCookie } from 'typescript-cookie';

export default function Player() {
  // Hooks
  const { id: sessionID } = useParams();

  // Session
  const state = useState();
  const session = useSession();
  const player = usePlayer();

  function renderGameState() {
    // State Base: Loading / Disconnected
    if (state === 'initializing') {
      return (
        <div className="flex items-center justify-center h-screen">
          <LoadingDots />
        </div>
      );
    }

    // State Base: Disconnected
    if (state === 'disconnected') {
      return (
        <IntermidiateStep
          title="Disconnected"
          subtitle="Please check your internet connection and refresh the page."
        />
      );
    }

    // State 1: No player
    if (!player?.id) {
      const existingPlayer = JSON.parse(getCookie(`player-${sessionID}`) ?? '{}');
      if (existingPlayer?.sessionId === sessionID) {
        return <></>;
      } else {
        return <PlayerSetup />;
      }
    }

    // State 3: Session loading
    if (!session) {
      return (
        <div className="flex items-center justify-center h-screen">
          <LoadingDots />
        </div>
      );
    }

    // State 4: Session created, waiting for players
    if (session.status == SessionStatus.created) {
      return (
        <IntermidiateStep
          showLoadingDots
          title="Players Joining"
          subtitle="A few more seconds now!"
        />
      );
    }

    // State 5: Session ending or ended
    if (session.status == SessionStatus.ending || session.status == SessionStatus.ended) {
      return <PlayerEndGame />;
    }

    // State 6: Drawing or Sharing phase
    if (session.status === SessionStatus.drawing || session.status == SessionStatus.sharing) {
      if (player.gameStatus === PlayerGameStatus.DRAWING) {
        return <PlayerCanvas />;
      }

      if (session.status == SessionStatus.sharing) {
        return <PlayerSpotlight isSharing={player?.gameStatus === PlayerGameStatus.SHARING} />;
      }

      if (player.gameStatus === PlayerGameStatus.SUBMITTED) {
        return (
          <IntermidiateStep
            showLoadingDots
            title="Players Still Drawing"
            subtitle="A few more seconds now!"
          />
        );
      }
    }

    // Default state (should not be reached in normal flow)
    return <IntermidiateStep showLoadingDots />;
  }

  return (
    <NoSleepProvider>
      <div className="h-screen">{renderGameState()}</div>
    </NoSleepProvider>
  );
}
